import { Point } from './types'
import { MapElement, MapSettings } from '../../../types'

const snapToGrid = (point: Point, mapSettings: MapSettings): Point => {
  if (!mapSettings.snapToGrid) return point

  const gridSize = mapSettings.gridSize
  // More aggressive snapping - use floor + 0.5 to snap to nearest grid line
  return {
    x: Math.round(point.x / gridSize) * gridSize,
    y: Math.round(point.y / gridSize) * gridSize,
  }
}

const getElementSnapPoints = (element: MapElement): Point[] => {
  const points: Point[] = []

  switch (element.type) {
    case 'wall':
    case 'door':
    case 'window':
    case 'line':
      if (element.properties.endPosition) {
        points.push(element.position)
        points.push(element.properties.endPosition)
        // Add midpoint
        points.push({
          x: (element.position.x + element.properties.endPosition.x) / 2,
          y: (element.position.y + element.properties.endPosition.y) / 2,
        })
      }
      break
    case 'room':
    case 'stairs':
    case 'rectangle':
      if (element.properties.width && element.properties.height) {
        const { x, y } = element.position
        const w = element.properties.width
        const h = element.properties.height
        // Corners
        points.push({ x, y })
        points.push({ x: x + w, y })
        points.push({ x: x + w, y: y + h })
        points.push({ x, y: y + h })
        // Midpoints of edges
        points.push({ x: x + w / 2, y })
        points.push({ x: x + w, y: y + h / 2 })
        points.push({ x: x + w / 2, y: y + h })
        points.push({ x, y: y + h / 2 })
      }
      break
    case 'circle':
      if (element.properties.radius) {
        const { x, y } = element.position
        const r = element.properties.radius
        points.push({ x: x + r, y: y + r }) // Center
        points.push({ x: x + r, y }) // Top
        points.push({ x: x + 2 * r, y: y + r }) // Right
        points.push({ x: x + r, y: y + 2 * r }) // Bottom
        points.push({ x, y: y + r }) // Left
      }
      break
  }

  return points
}

const snapToElements = (
  point: Point,
  elements: MapElement[],
  mapSettings: MapSettings,
): Point => {
  const SNAP_DISTANCE = 15 // Increased snap distance for better snapping
  let snappedPoint = { ...point }
  let minDistance = SNAP_DISTANCE

  // Get all elements to snap to
  const currentFloorElements = elements.filter(
    el => (el.floor || mapSettings.currentFloor) === mapSettings.currentFloor,
  )

  for (const element of currentFloorElements) {
    // Get snap points for this element
    const snapPoints = getElementSnapPoints(element)

    for (const snapPoint of snapPoints) {
      const distance = Math.sqrt(
        Math.pow(point.x - snapPoint.x, 2) + Math.pow(point.y - snapPoint.y, 2),
      )

      if (distance < minDistance) {
        snappedPoint = snapPoint
        minDistance = distance
      }
    }
  }

  return snappedPoint
}

const applySnapping = (
  point: Point,
  elements: MapElement[],
  mapSettings: MapSettings,
): Point => {
  // First try to snap to elements
  const elementSnapped = snapToElements(point, elements, mapSettings)

  // If element snapping occurred, use that
  if (elementSnapped.x !== point.x || elementSnapped.y !== point.y) {
    return elementSnapped
  }

  // Otherwise, apply grid snapping
  return snapToGrid(point, mapSettings)
}

export { snapToGrid, getElementSnapPoints, snapToElements, applySnapping }
